/** The next, lighter settings to try when a GIF is over the size limit: fewer frames first, then a narrower picture. */
export function shrinkGifSettings(width: number, fps: number): { width: number; fps: number } | null {
  if (fps > 8) return { width, fps: fps > 12 ? 12 : 8 };
  if (width > 200) return { width: Math.max(160, Math.round((width * 0.75) / 10) * 10), fps: fps > 6 ? 6 : fps };
  if (fps > 5) return { width, fps: 5 };
  return null;
}

/**
 * Picks the settings for a size limit from a SHORT sample instead of encoding the whole clip over and over:
 * `measure` encodes a sample of `sampleSeconds` at the given settings and returns its size in bytes; sizes scale
 * with length, so the full size is estimated as sample * (length / sampleSeconds). The first setting whose estimate
 * (with a 10% safety margin) fits is used. Returns the settings, the estimate, and what length would fit if none did.
 */
export async function fitGifSettings(
  measure: (s: { width: number; fps: number }) => Promise<number>,
  start: { width: number; fps: number },
  lengthSeconds: number,
  sampleSeconds: number,
  limitBytes: number,
  maxSteps = 8,
): Promise<{ width: number; fps: number; estimate: number; fits: boolean; fitSeconds: number }> {
  let cur = start;
  let estimate = 0;
  for (let i = 0; i < maxSteps; i++) {
    estimate = ((await measure(cur)) * lengthSeconds) / Math.max(0.1, sampleSeconds);
    if (estimate * 1.1 <= limitBytes) {
      // Fits. If it fits with lots of room to spare, a wider picture may fit too: GIF size grows roughly with the
      // picture's area, so try the width that should land near 92% of the limit, and keep it only if it measures in.
      const room = (0.92 * limitBytes) / (estimate * 1.1);
      if (i > 0 && room > 1.15) {
        const wider = Math.min(start.width, Math.round((cur.width * Math.sqrt(room)) / 10) * 10);
        if (wider > cur.width) {
          const cand = { width: wider, fps: cur.fps };
          const est2 = ((await measure(cand)) * lengthSeconds) / Math.max(0.1, sampleSeconds);
          if (est2 * 1.1 <= limitBytes) return { ...cand, estimate: est2, fits: true, fitSeconds: lengthSeconds };
        }
      }
      return { ...cur, estimate, fits: true, fitSeconds: lengthSeconds };
    }
    const next = shrinkGifSettings(cur.width, cur.fps);
    if (!next) break;
    cur = next;
  }
  const fitSeconds = Math.max(0.5, Math.floor(((lengthSeconds * limitBytes) / (estimate * 1.1)) * 2) / 2);
  return { ...cur, estimate, fits: false, fitSeconds };
}

/** Keeps Start and Length inside the clip: Start in [0, duration - 0.5], Length in [0.5, duration - Start].
 * With an unknown duration only the lower bounds apply. */
export function clampGifRange(start: number, length: number, duration: number | null): { start: number; length: number; changed: boolean } {
  let s = Number.isFinite(start) ? Math.max(0, start) : 0;
  let l = Number.isFinite(length) && length > 0 ? length : 5;
  l = Math.max(0.5, l);
  if (duration && Number.isFinite(duration)) {
    s = Math.min(s, Math.max(0, duration - 0.5));
    l = Math.max(Math.min(l, duration - s), Math.min(0.5, duration));
  }
  s = Math.round(s * 10) / 10; l = Math.round(l * 10) / 10;
  return { start: s, length: l, changed: s !== start || l !== length };
}
